// Long description editor (PRODUCT-001, 1022): a compact rich-text popup opened from "Add a long
// description" in Add product. Text styles and headings, colour and highlight, alignment, lists and
// indents, quotes and code, links, tables, divider lines and photos (uploaded or from the media
// library), plus an HTML source view. The HTML is cleaned with the same allow-list the server applies
// on write (product `sanitizeHtml`, rich mode), so the editor shows what the store will show. Photos
// are kept as <img data-asset="med_…">: the editor points them at the admin file route, the
// storefront at its own image URLs.
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { Button, cx, Icon, Popup, useToast, type IconName } from "@ady/ui";
import { ApiError } from "../api/client";
import { uploadPhoto, UploadRejected } from "../media/upload";
import { catalogKeys } from "./api";
import { LibraryPicker } from "./ProductEditor";
import { catalogError, useStoreIds } from "./ui";

// ---------------------------------------------------------------- cleaning (mirrors the server)

/** The server's cap for `long_description_html`. */
export const MAX_LONG_HTML = 60_000;

const ALLOWED = new Set(
  "p br hr strong b em i u s del ins sub sup small mark span div ul ol li h2 h3 h4 h5 h6 blockquote code pre a table thead tbody tfoot tr th td caption dl dt dd img".split(
    " ",
  ),
);
/** Dropped with everything inside them. */
const DROP = new Set(
  "script style iframe object embed noscript template svg math textarea title xmp noembed noframes plaintext select option optgroup head meta link base form input button frame frameset applet audio video source track canvas picture param portal dialog".split(
    " ",
  ),
);
const ASSET = /^med_[0-9A-Za-z]{10,40}$/;
const SAFE_HREF = /^(?:https:\/\/[^\s"'<>`\\]+|mailto:[^\s"'<>`\\]+)$/i;
const COLOUR =
  /^(?:#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\))$/i;

/** Keeps only alignment, colour, highlight and indent (and a photo's width). */
function safeStyle(style: string, tag: string): string {
  const out = new Map<string, string>();
  for (const decl of style.split(";")) {
    const i = decl.indexOf(":");
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const val = decl
      .slice(i + 1)
      .trim()
      .toLowerCase();
    if (tag === "img") {
      if (prop === "width" && /^(?:[1-9]\d?|100)%$/.test(val)) out.set(prop, val);
    } else if (prop === "text-align" && /^(?:left|center|right|justify)$/.test(val))
      out.set(prop, val);
    else if ((prop === "color" || prop === "background-color") && COLOUR.test(val))
      out.set(prop, val);
    else if (prop === "margin-left" && /^\d{1,3}px$/.test(val) && parseInt(val, 10) <= 400)
      out.set(prop, val);
  }
  return [...out].map(([k, v]) => `${k}: ${v}`).join("; ");
}

function cleanTree(parent: Element, doc: Document, srcFor: ((id: string) => string) | null) {
  for (const node of [...parent.childNodes]) {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    let el = node as HTMLElement;
    let tag = el.tagName.toLowerCase();
    if (DROP.has(tag)) {
      el.remove();
      continue;
    }
    if (tag === "h1") {
      const h2 = doc.createElement("h2");
      h2.append(...el.childNodes);
      const style = el.getAttribute("style");
      if (style) h2.setAttribute("style", style);
      el.replaceWith(h2);
      el = h2;
      tag = "h2";
    }
    // Word processors wrap whole pastes in <b style="font-weight:normal">: that is not bold.
    const unbold =
      (tag === "b" || tag === "strong") &&
      /font-weight:\s*(?:normal|[1-4]00)/i.test(el.getAttribute("style") ?? "");
    if (!ALLOWED.has(tag) || unbold) {
      cleanTree(el, doc, srcFor);
      el.replaceWith(...el.childNodes);
      continue;
    }
    // Text styles pasted from documents arrive as CSS: keep them as tags the allow-list knows.
    const css = (el.getAttribute("style") ?? "").toLowerCase();
    if (css && tag !== "img") {
      const wraps: string[] = [];
      const heavy = ["b", "strong", "h2", "h3", "h4", "h5", "h6", "th"];
      if (/font-weight:\s*(?:bold|[6-9]00)/.test(css) && !heavy.includes(tag)) wraps.push("strong");
      if (/font-style:\s*italic/.test(css) && !["i", "em"].includes(tag)) wraps.push("em");
      if (/text-decoration[^;]*underline/.test(css) && tag !== "u") wraps.push("u");
      if (/text-decoration[^;]*line-through/.test(css) && !["s", "del"].includes(tag))
        wraps.push("s");
      for (const w of wraps) {
        const inner = doc.createElement(w);
        inner.append(...el.childNodes);
        el.append(inner);
      }
    }
    const href = tag === "a" ? (el.getAttribute("href") ?? "").trim() : "";
    const style = safeStyle(el.getAttribute("style") ?? "", tag);
    const asset = tag === "img" ? (el.getAttribute("data-asset") ?? "") : "";
    const alt = tag === "img" ? (el.getAttribute("alt") ?? "").slice(0, 300) : "";
    for (const name of el.getAttributeNames()) el.removeAttribute(name);
    if (tag === "img") {
      if (!ASSET.test(asset)) {
        el.remove();
        continue;
      }
      el.setAttribute("data-asset", asset);
      el.setAttribute("alt", alt);
      if (srcFor) el.setAttribute("src", srcFor(asset));
    }
    if (tag === "a" && SAFE_HREF.test(href)) {
      el.setAttribute("href", href);
      el.setAttribute("rel", "noopener nofollow ugc");
    }
    if (style) el.setAttribute("style", style);
    cleanTree(el, doc, srcFor);
  }
}

/** Allow-listed HTML. With `srcFor`, photos get a viewable `src` (editor); without, none (stored). */
export function cleanRichHtml(
  html: string,
  srcFor: ((id: string) => string) | null = null,
): string {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, "text/html");
  cleanTree(doc.body, doc, srcFor);
  // Browsers leave empty paragraphs around lists; a stored text also drops its trailing blank lines.
  for (const para of doc.body.querySelectorAll("p")) if (!para.childNodes.length) para.remove();
  if (!srcFor) {
    let last = doc.body.lastElementChild;
    while (last?.tagName === "P" && !last.textContent?.trim() && !last.querySelector("img")) {
      last.remove();
      last = doc.body.lastElementChild;
    }
  }
  return doc.body.innerHTML.trim();
}

/** True when the HTML has no words, photos, tables or dividers. */
export function richIsEmpty(html: string): boolean {
  if (!html) return true;
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, "text/html");
  return !doc.body.textContent?.trim() && !doc.body.querySelector("img,table,hr");
}

/** The admin file route for a photo (works for every purpose, including private ones). */
export const adminPhotoSrc = (orgId: string) => (id: string) =>
  `/api/v1/orgs/${orgId}/media/${id}/file/master`;

// ---------------------------------------------------------------- the popup

const TEXT_COLOURS = [
  "#111827",
  "#4b5563",
  "#9ca3af",
  "#b42318",
  "#c4320a",
  "#b54708",
  "#067647",
  "#026aa2",
  "#155eef",
  "#6941c6",
];
const HIGHLIGHTS = ["#fef08a", "#fde68a", "#bbf7d0", "#bae6fd", "#ddd6fe", "#fecdd3"];
const BLOCKS = ["p", "h2", "h3", "h4", "blockquote", "pre"] as const;
type Block = (typeof BLOCKS)[number];
const STATES = [
  "bold",
  "italic",
  "underline",
  "strikeThrough",
  "subscript",
  "superscript",
  "insertUnorderedList",
  "insertOrderedList",
  "justifyLeft",
  "justifyCenter",
  "justifyRight",
  "justifyFull",
] as const;
type StateCmd = (typeof STATES)[number];

export function LongDescriptionDialog({
  open,
  value,
  onClose,
  onSave,
}: {
  open: boolean;
  /** Stored (clean) HTML. */
  value: string;
  onClose: () => void;
  onSave: (html: string) => void;
}) {
  // The editor decides whether closing needs a "discard changes?" check.
  const guard = useRef<() => void>(onClose);
  return (
    <Popup
      open={open}
      onClose={() => guard.current()}
      size="lg"
      label={t("catalog.rich.title")}
      data-testid="catalog-long-dialog"
    >
      {open ? <Editor initial={value} onClose={onClose} onSave={onSave} guard={guard} /> : null}
    </Popup>
  );
}

function Editor({
  initial,
  onClose,
  onSave,
  guard,
}: {
  initial: string;
  onClose: () => void;
  onSave: (html: string) => void;
  guard: { current: () => void };
}) {
  const { orgId, storeId } = useStoreIds();
  const toast = useToast();
  const qc = useQueryClient();
  const srcFor = adminPhotoSrc(orgId);
  const ed = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const saved = useRef<Range | null>(null);

  const [dirty, setDirty] = useState(false);
  const [asking, setAsking] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [active, setActive] = useState<Partial<Record<StateCmd, boolean>>>({});
  const [block, setBlock] = useState<string>("p");
  const [inTable, setInTable] = useState(false);
  const [menu, setMenu] = useState<"color" | "highlight" | "image" | "link" | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [alt, setAlt] = useState("");
  const [picking, setPicking] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [stats, setStats] = useState({ words: 0, chars: 0, size: 0, empty: true });

  guard.current = () => (dirty ? setAsking(true) : onClose());

  const measure = () => {
    const el = ed.current;
    if (!el) return;
    const text = el.innerText.replace(/\u200B/g, "").trim();
    const stored = cleanRichHtml(el.innerHTML);
    setStats({
      words: text ? text.split(/\s+/).length : 0,
      chars: text.length,
      size: stored.length,
      empty: !text && !el.querySelector("img,table,hr"),
    });
  };

  useEffect(() => {
    const el = ed.current;
    if (!el) return;
    el.innerHTML = cleanRichHtml(initial, srcFor) || "<p><br></p>";
    document.execCommand("defaultParagraphSeparator", false, "p");
    measure();
    el.focus();
  }, []);

  // Track the caret: remember it (toolbar inputs take the focus away) and light the active buttons.
  useEffect(() => {
    const onSel = () => {
      const el = ed.current;
      const sel = window.getSelection();
      if (!el || !sel?.rangeCount || !el.contains(sel.anchorNode)) return;
      saved.current = sel.getRangeAt(0).cloneRange();
      const next: Partial<Record<StateCmd, boolean>> = {};
      for (const c of STATES) next[c] = document.queryCommandState(c);
      setActive(next);
      const b = String(document.queryCommandValue("formatBlock") || "p").toLowerCase();
      setBlock((BLOCKS as readonly string[]).includes(b) ? b : "p");
      const at = sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode?.parentElement;
      setInTable(!!at?.closest("td,th") && el.contains(at.closest("td,th")));
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  // The selected photo shows an outline while its tools are open.
  useEffect(() => {
    if (!photo) return;
    photo.setAttribute("data-selected", "");
    setAlt(photo.getAttribute("alt") ?? "");
    return () => photo.removeAttribute("data-selected");
  }, [photo]);

  const changed = () => {
    setDirty(true);
    measure();
  };

  const restore = () => {
    const el = ed.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    let range = saved.current;
    if (!range || !el.contains(range.startContainer)) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const run = (cmd: string, arg?: string) => {
    restore();
    // Colours need inline styles; everything else uses tags (<b>, <i>, <u>…) the allow-list keeps.
    document.execCommand(
      "styleWithCSS",
      false,
      cmd === "foreColor" || cmd === "hiliteColor" ? "true" : "false",
    );
    document.execCommand(cmd, false, arg);
    setMenu(null);
    changed();
    document.dispatchEvent(new Event("selectionchange"));
  };

  const setBlockStyle = (b: Block) => {
    // Toggling the same quote or code block again returns it to a paragraph.
    run("formatBlock", `<${b === block && b !== "p" ? "p" : b}>`);
  };

  // Photos and tables go in through the DOM: Chrome's insertHTML misplaces them inside lists.
  const caretRange = () => {
    restore();
    const sel = window.getSelection();
    return sel?.rangeCount ? sel.getRangeAt(0) : null;
  };
  const placeCaret = (node: Node, atStart: boolean) => {
    const r = document.createRange();
    if (atStart) r.setStart(node, 0);
    else r.setStartAfter(node);
    r.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
    saved.current = r.cloneRange();
  };
  /** The editor's top-level block that holds the caret, or null. */
  const topBlock = (node: Node | null) => {
    const el = ed.current;
    let n = node;
    while (n && n.parentNode !== el) n = n.parentNode;
    return n && n !== el ? (n as Element) : null;
  };

  const insertPhotos = (ids: string[]) => {
    const el = ed.current;
    const range = ids.length ? caretRange() : null;
    if (!el || !range) return;
    const frag = document.createDocumentFragment();
    for (const id of ids) {
      const img = document.createElement("img");
      img.setAttribute("data-asset", id);
      img.setAttribute("alt", "");
      img.setAttribute("src", srcFor(id));
      frag.append(img);
    }
    const last = frag.lastChild!;
    const at =
      range.startContainer instanceof Element
        ? range.startContainer
        : range.startContainer.parentElement;
    const inline = at?.closest("li,td,th");
    if (inline && el.contains(inline)) {
      // Inside a list item or a table cell the photo goes right at the caret.
      range.insertNode(frag);
      placeCaret(last, false);
    } else {
      // Elsewhere it gets its own paragraph, under the text the caret is in (or in place of an empty one).
      const para = document.createElement("p");
      para.append(frag);
      const after = document.createElement("p");
      after.innerHTML = "<br>";
      const block = topBlock(range.startContainer);
      if (!block) el.append(para, after);
      else if (!block.textContent?.trim() && !block.querySelector("img,table,hr"))
        block.replaceWith(para, after);
      else block.after(para, after);
      placeCaret(after, true);
    }
    setMenu(null);
    changed();
  };

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = "";
    if (!files.length) return;
    setMenu(null);
    const ids: string[] = [];
    setProgress({ done: 0, total: files.length });
    for (const [i, f] of files.entries()) {
      try {
        const asset = await uploadPhoto(orgId, storeId, f, crypto.randomUUID());
        ids.push(asset.id);
      } catch (err) {
        const key =
          err instanceof UploadRejected
            ? err.reason === "type"
              ? "catalog.media.typeNotAllowed"
              : "catalog.media.tooLarge"
            : "catalog.media.uploadFailed";
        toast.show({
          tone: "error",
          title: t(key, { name: f.name }),
          ...(err instanceof ApiError ? { body: catalogError(err) } : {}),
        });
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setProgress(null);
    insertPhotos(ids);
    void qc.invalidateQueries({ queryKey: catalogKeys.media(orgId) });
  };

  const insertTable = () => {
    const el = ed.current;
    const range = caretRange();
    if (!el || !range) return;
    const table = document.createElement("table");
    const body = table.createTBody();
    for (let r = 0; r < 3; r++) {
      const row = body.insertRow();
      for (let c = 0; c < 3; c++) row.insertCell().innerHTML = "<br>";
    }
    const after = document.createElement("p");
    after.innerHTML = "<br>";
    const block = topBlock(range.startContainer);
    if (!block) el.append(table, after);
    else if (!block.textContent?.trim() && !block.querySelector("img"))
      block.replaceWith(table, after);
    else block.after(table, after);
    placeCaret(body.rows[0]!.cells[0]!, true);
    changed();
    document.dispatchEvent(new Event("selectionchange"));
  };

  const currentCell = () => {
    const r = saved.current;
    const n = r?.startContainer;
    const at = n instanceof Element ? n : n?.parentElement;
    const cell = at?.closest("td,th") as HTMLTableCellElement | null;
    return cell && ed.current?.contains(cell) ? cell : null;
  };

  const tableOp = (op: "row" | "col" | "delRow" | "delCol" | "delTable") => {
    const cell = currentCell();
    const row = cell?.parentElement as HTMLTableRowElement | null;
    const table = cell?.closest("table");
    if (!cell || !row || !table) return;
    const col = cell.cellIndex;
    if (op === "row") {
      const copy = row.cloneNode(true) as HTMLTableRowElement;
      for (const c of copy.cells) c.innerHTML = "<br>";
      row.after(copy);
    } else if (op === "col") {
      for (const r of table.rows) {
        const ref = r.cells[Math.min(col, r.cells.length - 1)];
        const c = document.createElement(ref?.tagName.toLowerCase() === "th" ? "th" : "td");
        c.innerHTML = "<br>";
        if (ref) ref.after(c);
        else r.append(c);
      }
    } else if (op === "delRow") {
      if (table.rows.length <= 1) table.remove();
      else row.remove();
    } else if (op === "delCol") {
      if (row.cells.length <= 1) table.remove();
      else for (const r of [...table.rows]) r.cells[col]?.remove();
    } else table.remove();
    setInTable(op === "row" || op === "col");
    changed();
  };

  const openLink = () => {
    const a = currentLink();
    setLinkUrl(a?.getAttribute("href") ?? "");
    setLinkError(null);
    setMenu(menu === "link" ? null : "link");
  };
  const currentLink = () => {
    const n = saved.current?.startContainer;
    const at = n instanceof Element ? n : n?.parentElement;
    const a = at?.closest("a");
    return a && ed.current?.contains(a) ? a : null;
  };
  const applyLink = () => {
    let url = linkUrl.trim();
    if (url && !/^[a-z]+:/i.test(url)) url = `https://${url}`;
    if (!SAFE_HREF.test(url)) {
      setLinkError(t("catalog.rich.linkBad"));
      return;
    }
    const a = currentLink();
    if (a) {
      a.setAttribute("href", url);
      setMenu(null);
      changed();
      return;
    }
    if (!saved.current || saved.current.collapsed) {
      setLinkError(t("catalog.rich.linkNeedsText"));
      return;
    }
    run("createLink", url);
  };

  const toSource = () => {
    setPhoto(null);
    setMenu(null);
    setSource(
      cleanRichHtml(ed.current?.innerHTML ?? "").replace(
        /(<\/(?:p|h\d|li|tr|table|ul|ol|blockquote|pre)>)/g,
        "$1\n",
      ),
    );
  };
  const fromSource = () => {
    const html = source ?? "";
    setSource(null);
    // The editor is mounted again on the next render; fill it then.
    requestAnimationFrame(() => {
      if (!ed.current) return;
      ed.current.innerHTML = cleanRichHtml(html, srcFor) || "<p><br></p>";
      changed();
    });
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    e.preventDefault();
    if (html) run("insertHTML", cleanRichHtml(html, srcFor));
    else run("insertText", text);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // Tab moves between table cells; Tab in the last cell adds a row.
    const cell = e.key === "Tab" ? currentCell() : null;
    if (cell) {
      e.preventDefault();
      const table = cell.closest("table")!;
      if (!e.shiftKey && cell === table.querySelector("tr:last-child > :last-child"))
        tableOp("row");
      const cells = [...table.querySelectorAll("td,th")];
      const next = cells[cells.indexOf(cell) + (e.shiftKey ? -1 : 1)];
      if (next) placeCaret(next, true);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      openLink();
    }
  };

  const save = () => {
    const html =
      source !== null ? cleanRichHtml(source) : cleanRichHtml(ed.current?.innerHTML ?? "");
    onSave(richIsEmpty(html) ? "" : html);
    onClose();
  };

  const tooLong = stats.size > MAX_LONG_HTML;
  const keep = (e: { preventDefault: () => void }) => e.preventDefault();
  const off = source !== null;

  return (
    <div className="flex flex-col gap-2">
      <header className="flex items-start gap-2">
        <span className="ady-bg-brand flex size-7 shrink-0 items-center justify-center rounded-xs text-on-primary">
          <Icon name="file" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-sm font-semibold">{t("catalog.rich.title")}</h2>
          <span className="text-xs text-fg-tertiary">{t("catalog.rich.subtitle")}</span>
        </span>
        <button
          type="button"
          onClick={() => guard.current()}
          title={t("catalog.rich.cancel")}
          className="flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-sunken"
        >
          <Icon name="x" />
          <span className="ady-sr-only">{t("catalog.rich.cancel")}</span>
        </button>
      </header>

      <div className="overflow-hidden rounded-sm border border-control bg-surface">
        <div
          role="toolbar"
          aria-label={t("catalog.rich.toolbar")}
          className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-card bg-sunken px-1.5 py-1"
        >
          <Tool
            icon="undo"
            label={t("catalog.rich.undo")}
            disabled={off}
            onClick={() => run("undo")}
          />
          <Tool
            icon="redo"
            label={t("catalog.rich.redo")}
            disabled={off}
            onClick={() => run("redo")}
          />
          <Sep />
          <select
            value={block}
            disabled={off}
            onChange={(e) => setBlockStyle(e.target.value as Block)}
            aria-label={t("catalog.rich.blockStyle")}
            title={t("catalog.rich.blockStyle")}
            className="min-h-control rounded-xs border border-control bg-surface px-1.5 text-xs disabled:opacity-50"
          >
            {BLOCKS.map((b) => (
              <option key={b} value={b}>
                {t(`catalog.rich.blocks.${b}`)}
              </option>
            ))}
          </select>
          <Sep />
          <Tool
            icon="bold"
            label={t("catalog.rich.bold")}
            on={active.bold}
            disabled={off}
            onClick={() => run("bold")}
          />
          <Tool
            icon="italic"
            label={t("catalog.rich.italic")}
            on={active.italic}
            disabled={off}
            onClick={() => run("italic")}
          />
          <Tool
            icon="underline"
            label={t("catalog.rich.underline")}
            on={active.underline}
            disabled={off}
            onClick={() => run("underline")}
          />
          <Tool
            icon="strike"
            label={t("catalog.rich.strike")}
            on={active.strikeThrough}
            disabled={off}
            onClick={() => run("strikeThrough")}
          />
          <Tool
            icon="subscript"
            label={t("catalog.rich.sub")}
            on={active.subscript}
            disabled={off}
            onClick={() => run("subscript")}
          />
          <Tool
            icon="superscript"
            label={t("catalog.rich.sup")}
            on={active.superscript}
            disabled={off}
            onClick={() => run("superscript")}
          />
          <Sep />
          <span className="relative">
            <Tool
              icon="textColor"
              label={t("catalog.rich.textColor")}
              on={menu === "color"}
              disabled={off}
              onClick={() => setMenu(menu === "color" ? null : "color")}
            />
            {menu === "color" ? (
              <Palette
                colours={TEXT_COLOURS}
                label={t("catalog.rich.textColor")}
                onPick={(c) => run("foreColor", c)}
              />
            ) : null}
          </span>
          <span className="relative">
            <Tool
              icon="highlight"
              label={t("catalog.rich.highlight")}
              on={menu === "highlight"}
              disabled={off}
              onClick={() => setMenu(menu === "highlight" ? null : "highlight")}
            />
            {menu === "highlight" ? (
              <Palette
                colours={HIGHLIGHTS}
                label={t("catalog.rich.highlight")}
                onPick={(c) => run("hiliteColor", c)}
                onNone={() => run("hiliteColor", "transparent")}
              />
            ) : null}
          </span>
          <Sep />
          <Tool
            icon="alignLeft"
            label={t("catalog.rich.alignLeft")}
            on={active.justifyLeft}
            disabled={off}
            onClick={() => run("justifyLeft")}
          />
          <Tool
            icon="alignCenter"
            label={t("catalog.rich.alignCenter")}
            on={active.justifyCenter}
            disabled={off}
            onClick={() => run("justifyCenter")}
          />
          <Tool
            icon="alignRight"
            label={t("catalog.rich.alignRight")}
            on={active.justifyRight}
            disabled={off}
            onClick={() => run("justifyRight")}
          />
          <Tool
            icon="alignJustify"
            label={t("catalog.rich.alignJustify")}
            on={active.justifyFull}
            disabled={off}
            onClick={() => run("justifyFull")}
          />
          <Sep />
          <Tool
            icon="list"
            label={t("catalog.rich.bullets")}
            on={active.insertUnorderedList}
            disabled={off}
            onClick={() => run("insertUnorderedList")}
          />
          <Tool
            icon="listOrdered"
            label={t("catalog.rich.numbers")}
            on={active.insertOrderedList}
            disabled={off}
            onClick={() => run("insertOrderedList")}
          />
          <Tool
            icon="outdent"
            label={t("catalog.rich.outdent")}
            disabled={off}
            onClick={() => run("outdent")}
          />
          <Tool
            icon="indent"
            label={t("catalog.rich.indent")}
            disabled={off}
            onClick={() => run("indent")}
          />
          <Sep />
          <Tool
            icon="quote"
            label={t("catalog.rich.blocks.blockquote")}
            on={block === "blockquote"}
            disabled={off}
            onClick={() => setBlockStyle("blockquote")}
          />
          <Tool
            icon="code"
            label={t("catalog.rich.blocks.pre")}
            on={block === "pre"}
            disabled={off}
            onClick={() => setBlockStyle("pre")}
          />
          <Tool
            icon="link"
            label={t("catalog.rich.link")}
            on={menu === "link"}
            disabled={off}
            onClick={openLink}
          />
          <Tool
            icon="unlink"
            label={t("catalog.rich.unlink")}
            disabled={off}
            onClick={() => run("unlink")}
          />
          <Sep />
          <span className="relative">
            <Tool
              icon="image"
              label={t("catalog.rich.image")}
              on={menu === "image"}
              disabled={off || !!progress}
              onClick={() => setMenu(menu === "image" ? null : "image")}
            />
            {menu === "image" ? (
              <span className="absolute start-0 top-full z-20 mt-1 flex w-max flex-col rounded-sm border border-card bg-surface p-1 shadow-2">
                <MenuItem icon="upload" onClick={() => fileInput.current?.click()}>
                  {t("catalog.rich.imageUpload")}
                </MenuItem>
                <MenuItem
                  icon="folder"
                  onClick={() => {
                    setMenu(null);
                    setPicking(true);
                  }}
                >
                  {t("catalog.rich.imageLibrary")}
                </MenuItem>
              </span>
            ) : null}
          </span>
          <Tool icon="table" label={t("catalog.rich.table")} disabled={off} onClick={insertTable} />
          <Tool
            icon="divider"
            label={t("catalog.rich.divider")}
            disabled={off}
            onClick={() => run("insertHorizontalRule")}
          />
          <Tool
            icon="eraser"
            label={t("catalog.rich.clear")}
            disabled={off}
            onClick={() => run("removeFormat")}
          />
          <span className="ms-auto" />
          <button
            type="button"
            onMouseDown={keep}
            onClick={() => (off ? fromSource() : toSource())}
            aria-pressed={off}
            className={cx(
              "flex min-h-control items-center gap-1 rounded-xs px-2 text-xs font-semibold",
              off ? "bg-surface text-link" : "text-fg-secondary hover:bg-surface",
            )}
          >
            <Icon name="code" />
            {off ? t("catalog.rich.visual") : t("catalog.rich.source")}
          </button>
        </div>

        {menu === "link" ? (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-card px-2 py-1.5">
            <Icon name="link" />
            <input
              autoFocus
              value={linkUrl}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setLinkError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenu(null);
                }
              }}
              placeholder={t("catalog.rich.linkPh")}
              aria-label={t("catalog.rich.link")}
              inputMode="url"
              className="min-h-control min-w-0 flex-1 rounded-xs border border-control bg-surface px-2 text-xs outline-none focus-visible:outline-2 focus-visible:outline-focus"
            />
            <Button size="sm" onClick={applyLink}>
              {t("catalog.rich.linkApply")}
            </Button>
            {currentLink() ? (
              <Button size="sm" variant="ghost" onClick={() => run("unlink")}>
                {t("catalog.rich.unlink")}
              </Button>
            ) : null}
            {linkError ? <span className="w-full text-xs text-danger-fg">{linkError}</span> : null}
          </div>
        ) : null}

        {inTable && !off ? (
          <div className="flex flex-wrap items-center gap-1 border-b border-card px-2 py-1 text-xs">
            <span className="me-1 font-semibold text-fg-tertiary">
              {t("catalog.rich.tableTools")}
            </span>
            <Chip onClick={() => tableOp("row")}>{t("catalog.rich.addRow")}</Chip>
            <Chip onClick={() => tableOp("col")}>{t("catalog.rich.addColumn")}</Chip>
            <Chip onClick={() => tableOp("delRow")}>{t("catalog.rich.deleteRow")}</Chip>
            <Chip onClick={() => tableOp("delCol")}>{t("catalog.rich.deleteColumn")}</Chip>
            <Chip danger onClick={() => tableOp("delTable")}>
              {t("catalog.rich.deleteTable")}
            </Chip>
          </div>
        ) : null}

        {photo && !off ? (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-card px-2 py-1 text-xs">
            <span className="font-semibold text-fg-tertiary">{t("catalog.rich.photoTools")}</span>
            <input
              value={alt}
              onChange={(e) => {
                setAlt(e.target.value);
                photo.setAttribute("alt", e.target.value.slice(0, 300));
                setDirty(true);
              }}
              placeholder={t("catalog.rich.imageAltPh")}
              aria-label={t("catalog.rich.imageAlt")}
              maxLength={300}
              className="min-h-control min-w-0 flex-1 rounded-xs border border-control bg-surface px-2 text-xs outline-none focus-visible:outline-2 focus-visible:outline-focus"
            />
            {(["s", "m", "l"] as const).map((k) => {
              const width = k === "s" ? "33%" : k === "m" ? "50%" : "100%";
              const on = photo.style.width === width;
              return (
                <Chip
                  key={k}
                  on={on}
                  onClick={() => {
                    photo.style.width = width;
                    changed();
                  }}
                >
                  {t(`catalog.rich.sizes.${k}`)}
                </Chip>
              );
            })}
            <Chip
              danger
              onClick={() => {
                photo.remove();
                setPhoto(null);
                changed();
              }}
            >
              {t("catalog.rich.removeImage")}
            </Chip>
          </div>
        ) : null}

        {progress ? (
          <p className="border-b border-card px-2 py-1 text-xs text-fg-secondary" role="status">
            {t("catalog.rich.uploading", progress)}
          </p>
        ) : null}

        {off ? (
          <textarea
            value={source ?? ""}
            onChange={(e) => {
              setSource(e.target.value);
              setDirty(true);
              setStats((s) => ({ ...s, size: e.target.value.length }));
            }}
            aria-label={t("catalog.rich.sourceLabel")}
            spellCheck={false}
            className="block h-[55vh] min-h-60 w-full resize-y bg-surface p-3 font-mono text-xs outline-none"
          />
        ) : (
          <div className="relative">
            {stats.empty ? (
              <p className="pointer-events-none absolute inset-x-3 top-3 text-sm text-fg-tertiary">
                {t("catalog.rich.placeholder")}
              </p>
            ) : null}
            <div
              ref={ed}
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              aria-label={t("catalog.rich.editorLabel")}
              onInput={changed}
              onPaste={onPaste}
              onKeyDown={onKey}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                setPhoto(target instanceof HTMLImageElement ? target : null);
                setMenu((m) => (m === "link" ? m : null));
              }}
              className={cx(
                "h-[55vh] min-h-60 overflow-y-auto p-3 text-sm outline-none",
                richContent,
              )}
            />
          </div>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(e) => void onFiles(e)}
      />
      <LibraryPicker
        open={picking}
        onClose={() => setPicking(false)}
        exclude={[]}
        onPick={(m) => {
          setPicking(false);
          insertPhotos(m.map((x) => x.asset_id));
        }}
      />

      {asking ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-sm border border-card bg-sunken px-2 py-1.5 text-xs"
          role="alert"
        >
          <span className="flex-1 font-semibold">{t("catalog.rich.discardTitle")}</span>
          <Button size="sm" variant="ghost" onClick={() => setAsking(false)}>
            {t("catalog.rich.keep")}
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            {t("catalog.rich.discard")}
          </Button>
        </div>
      ) : null}

      <footer className="flex flex-wrap items-center gap-2">
        <span className={cx("flex-1 text-xs", tooLong ? "text-danger-fg" : "text-fg-tertiary")}>
          {tooLong
            ? t("catalog.rich.tooLong", {
                size: stats.size.toLocaleString(),
                max: MAX_LONG_HTML.toLocaleString(),
              })
            : `${t("catalog.rich.words", { count: stats.words })} · ${t("catalog.rich.chars", { count: stats.chars })}`}
        </span>
        <Button variant="secondary" onClick={() => guard.current()}>
          {t("catalog.rich.cancel")}
        </Button>
        <Button
          icon="check"
          disabled={tooLong || !!progress}
          onClick={save}
          data-testid="catalog-long-save"
        >
          {t("catalog.rich.save")}
        </Button>
      </footer>
    </div>
  );
}

/** Typography for the long description (styles.css `.ady-rich`), in the editor and the preview. */
export const richContent = "ady-rich";

// ---------------------------------------------------------------- small parts

function Tool({
  icon,
  label,
  on,
  disabled,
  onClick,
}: {
  icon: IconName;
  label: string;
  on?: boolean | undefined;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      // Keep the caret in the text: the command runs on the current selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={on === undefined ? undefined : on}
      className={cx(
        "flex min-h-control min-w-control items-center justify-center rounded-xs disabled:opacity-40",
        on ? "bg-surface text-link shadow-1" : "text-fg-secondary hover:bg-surface hover:text-fg",
      )}
    >
      <Icon name={icon} />
    </button>
  );
}

const Sep = () => <span aria-hidden className="mx-0.5 h-5 w-px bg-control" />;

function Palette({
  colours,
  label,
  onPick,
  onNone,
}: {
  colours: string[];
  label: string;
  onPick: (c: string) => void;
  onNone?: () => void;
}) {
  return (
    <span
      className="absolute start-0 top-full z-20 mt-1 flex w-max flex-col gap-1.5 rounded-sm border border-card bg-surface p-2 shadow-2"
      onMouseDown={(e) => {
        if (!(e.target instanceof HTMLInputElement)) e.preventDefault();
      }}
    >
      <span
        className={cx("grid gap-1", colours.length > 6 ? "grid-cols-5" : "grid-cols-6")}
        role="group"
        aria-label={label}
      >
        {colours.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onPick(c)}
            title={c}
            aria-label={c}
            className="size-6 rounded-xs border border-card hover:scale-110"
            style={{ background: c }}
          />
        ))}
      </span>
      <label className="flex items-center gap-1.5 text-xs text-fg-secondary">
        <input
          type="color"
          onChange={(e) => onPick(e.target.value)}
          className="h-6 w-8 cursor-pointer rounded-xs border border-card bg-surface"
        />
        {t("catalog.rich.customColor")}
      </label>
      {onNone ? (
        <button
          type="button"
          onClick={onNone}
          className="self-start text-xs font-semibold text-link"
        >
          {t("catalog.rich.noHighlight")}
        </button>
      ) : null}
    </span>
  );
}

function MenuItem({
  icon,
  onClick,
  children,
}: {
  icon: IconName;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex min-h-control items-center gap-2 rounded-xs px-2 text-xs hover:bg-sunken"
    >
      <Icon name={icon} />
      {children}
    </button>
  );
}

function Chip({
  on,
  danger,
  onClick,
  children,
}: {
  on?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cx(
        "rounded-full border px-2 py-0.5 text-xs font-semibold",
        on ? "ady-outline-brand border-link text-link" : "border-control",
        danger ? "text-danger-fg hover:border-danger-fg" : "hover:border-link",
      )}
    >
      {children}
    </button>
  );
}
